/**
 * State that lives beside the cc-render fold: everything the runner and the
 * control plane add on top of the CLI's own stream. Same shape of contract as
 * the reducer — one pure function, plain data.
 */

import type { RequestPermissionSubject } from '@agentclientprotocol/sdk/experimental/v2'
import type {
  QueuedMessage,
  SessionRecord,
  SessionStatus,
} from '@web/api/types'

export type PermissionRequest = {
  requestId: string
  subject?: RequestPermissionSubject | null
  toolName: string
  input: Record<string, unknown>
  toolUseId: string | null
  title?: string | null
  displayName?: string | null
  description?: string | null
  options?: { optionId: string; name: string; kind: string }[]
  resolved?: {
    behavior: 'allow' | 'deny' | 'cancelled'
    message?: string | null
  }
  seq: number
}

export type ContextUsage = {
  totalTokens?: number
  maxTokens?: number
  percentage?: number
  categories?: { name: string; tokens: number; percentage?: number }[]
  [k: string]: unknown
}

export type StartupTiming = {
  seq: number
  /** When the control plane recorded the timing, in epoch seconds. */
  timestamp: number
  failed: boolean
  api: Record<string, unknown> | null
  runner: Record<string, unknown> | null
}

export type BridgeState = {
  startup: { phase: string; label: string; seq: number }[]
  /** Server-side breakdown of the latest start, once the runner has answered. */
  startupTiming: StartupTiming | null
  toolResults: Record<string, Record<string, any>>
  toolProgress: Record<string, ToolProgress>
  record: SessionRecord | null
  /** Retained independently of late lifecycle snapshots and replay. */
  totalCostUsd: number
  costEventIndex: number
  /** Historical frames through this sequence precede the latest hello snapshot. */
  snapshotSeq: number
  /** Newest lifecycle or queue observation; older record snapshots cannot replace it. */
  lifecycleSeq: number
  /** Durable lifecycle observations, including history replayed after a snapshot. */
  lifecycle: {
    seq: number
    status: string
    /** When the control plane recorded the lifecycle transition, in epoch seconds. */
    timestamp?: number
    error?: string
    /** Why the control plane made this transition, when it says (e.g. a runtime drain). */
    reason?: string
  }[]
  status: SessionStatus | 'unknown'
  error: string | null
  providerSessionId: string | null
  /** Keyed by request id, in arrival order. */
  permissions: Record<string, PermissionRequest>
  /** tool_use_id -> request id (latest). */
  permissionByToolUse: Record<string, string>
  contextUsage: ContextUsage | null
  mcpStatus: unknown | null
  stderr: string[]
  commandErrors: {
    command: string
    error: string
    seq: number
    requestId?: string
  }[]
  /** Messages the control plane is holding until the current turn ends. */
  queue: QueuedMessage[]
  /** The runtime accepted Stop for this turn and is waiting for the provider to wind down. */
  stopping: boolean
}

/** Durable app progress, correlated with the invocation in a real tool call. */
export type ToolProgress = {
  invocationId: string
  action: string
  percentage: number
  detail: string
  status: 'running' | 'completed'
}

export const initialBridgeState = (): BridgeState => ({
  startup: [],
  startupTiming: null,
  toolResults: {},
  toolProgress: {},
  record: null,
  totalCostUsd: 0,
  costEventIndex: 0,
  snapshotSeq: 0,
  lifecycleSeq: 0,
  lifecycle: [],
  status: 'unknown',
  error: null,
  providerSessionId: null,
  permissions: {},
  permissionByToolUse: {},
  contextUsage: null,
  mcpStatus: null,
  stderr: [],
  commandErrors: [],
  queue: [],
  stopping: false,
})

const MAX_STDERR = 400
const acpConnectionLostCommandError =
  "This session's connection was lost and can't accept new messages. Reconnect or start a new session to continue."
const SNAPSHOT_EVENTS = new Set([
  'bridge/prompt_started',
  'bridge/prompt_finished',
  'bridge/queue',
  'bridge/record',
  'bridge/session_status',
])

/** Ignore malformed values and keep authoritative cost projections monotonic. */
export function costHighWater(previous: number, value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.max(previous, value)
    : previous
}

export function applyBridgeFrame(
  s: BridgeState,
  frame: Record<string, unknown> & { type: string },
  seq: number,
  timestamp?: number,
): BridgeState {
  if (
    seq > 0 &&
    ['bridge/lifecycle', 'bridge/session_status'].includes(frame.type) &&
    [
      'created',
      'starting',
      'ready',
      'asleep',
      'exited',
      'closed',
      'detached',
      'error',
    ].includes(String(frame.status)) &&
    !s.lifecycle.some((event) => event.seq === seq)
  ) {
    s = {
      ...s,
      lifecycle: [
        ...s.lifecycle,
        {
          seq,
          status: String(frame.status),
          ...(typeof timestamp === 'number' &&
          Number.isFinite(timestamp) &&
          timestamp > 0
            ? { timestamp }
            : {}),
          ...(typeof frame.error === 'string' ? { error: frame.error } : {}),
          ...(typeof frame.reason === 'string' ? { reason: frame.reason } : {}),
        },
      ].sort((a, b) => a.seq - b.seq),
    }
  }
  // Reconstruct transcript events while retaining the control plane's current
  // lifecycle, configuration and queue from the snapshot preceding replay.
  if (seq > 0 && seq <= s.snapshotSeq && SNAPSHOT_EVENTS.has(frame.type))
    return s
  switch (frame.type) {
    case 'bridge/tool_progress': {
      if (
        typeof frame.invocation_id !== 'string' ||
        typeof frame.action !== 'string' ||
        typeof frame.percentage !== 'number' ||
        !Number.isFinite(frame.percentage) ||
        typeof frame.detail !== 'string' ||
        !['running', 'completed'].includes(String(frame.status))
      )
        return s
      const previous = s.toolProgress[frame.invocation_id]
      if (previous?.status === 'completed' && frame.status !== 'completed')
        return s
      return {
        ...s,
        toolProgress: {
          ...s.toolProgress,
          [frame.invocation_id]: {
            invocationId: frame.invocation_id,
            action: frame.action,
            percentage: Math.max(0, Math.min(100, frame.percentage)),
            detail: frame.detail,
            status: frame.status as ToolProgress['status'],
          },
        },
      }
    }
    case 'bridge/tool_result':
      return frame.verified === true && typeof frame.tool_call_id === 'string'
        ? {
            ...s,
            toolResults: { ...s.toolResults, [frame.tool_call_id]: frame },
          }
        : s
    case 'bridge/hello': {
      const record = frame.session as SessionRecord
      return {
        ...s,
        record,
        totalCostUsd: costHighWater(s.totalCostUsd, record.total_cost_usd),
        snapshotSeq: record.last_seq,
        lifecycleSeq: Math.max(s.lifecycleSeq, record.last_seq),
        status: record.status,
        error: record.error,
        providerSessionId:
          record.provider_session_id ??
          record.config.provider_session_id ??
          null,
        queue: record.queue ?? s.queue,
      }
    }
    case 'bridge/prompt_started':
      return {
        ...s,
        status: 'running',
        stopping: false,
        lifecycleSeq: Math.max(s.lifecycleSeq, seq),
      }
    case 'bridge/prompt_cancelling':
      return { ...s, stopping: true }
    case 'bridge/prompt_finished':
      return {
        ...s,
        status: frame.active_request_id ? 'running' : 'idle',
        stopping: false,
        lifecycleSeq: Math.max(s.lifecycleSeq, seq),
      }
    case 'bridge/queue':
      return {
        ...s,
        queue: (frame.items as QueuedMessage[]) ?? [],
        lifecycleSeq: Math.max(s.lifecycleSeq, seq),
      }
    case 'bridge/record': {
      // A delayed snapshot may carry a useful rename/configuration but predate a
      // completed turn or a dispatched queue item already observed on the socket.
      const incoming = frame.session as SessionRecord
      const stale =
        Number.isSafeInteger(incoming.last_seq) &&
        incoming.last_seq < s.lifecycleSeq
      const record = stale
        ? {
            ...incoming,
            status: s.status as SessionStatus,
            error: s.error,
            queue: s.queue,
          }
        : incoming
      return {
        ...s,
        record,
        lifecycleSeq: stale ? s.lifecycleSeq : Math.max(s.lifecycleSeq, seq),
        totalCostUsd: costHighWater(s.totalCostUsd, record.total_cost_usd),
        status: record.status,
        error: record.error,
        providerSessionId:
          record.provider_session_id ??
          record.config.provider_session_id ??
          null,
        queue: record.queue ?? s.queue,
      }
    }
    case 'bridge/cost_event': {
      const event = frame.event as
        | { event_index?: unknown; total_usd?: unknown }
        | undefined
      const index = event?.event_index
      return {
        ...s,
        totalCostUsd: costHighWater(
          costHighWater(s.totalCostUsd, frame.total_cost_usd),
          event?.total_usd,
        ),
        costEventIndex:
          typeof index === 'number' &&
          Number.isSafeInteger(index) &&
          index > s.costEventIndex
            ? index
            : s.costEventIndex,
      }
    }
    case 'bridge/session_status':
      return {
        ...s,
        lifecycleSeq: Math.max(s.lifecycleSeq, seq),
        status: (frame.status as SessionStatus) ?? s.status,
        error:
          (frame.error as string | null) ??
          (frame.status === 'error' ? s.error : null),
        providerSessionId:
          (frame.provider_session_id as string | null) ?? s.providerSessionId,
      }
    case 'bridge/permission_request': {
      const req: PermissionRequest = {
        requestId: String(frame.request_id),
        subject: frame.subject as RequestPermissionSubject | null,
        toolName: String(frame.title ?? 'Agent tool'),
        input: (frame.input as Record<string, unknown>) ?? {},
        toolUseId:
          (
            frame.subject as {
              type?: string
              toolCall?: { toolCallId?: string }
              toolCallId?: string
            } | null
          )?.toolCall?.toolCallId ??
          (frame.subject as { toolCallId?: string } | null)?.toolCallId ??
          null,
        title: frame.title as string | null,
        displayName: frame.display_name as string | null,
        description: frame.description as string | null,
        options: frame.options as PermissionRequest['options'],
        seq,
      }
      const permissionByToolUse = req.toolUseId
        ? { ...s.permissionByToolUse, [req.toolUseId]: req.requestId }
        : s.permissionByToolUse
      return {
        ...s,
        permissions: { ...s.permissions, [req.requestId]: req },
        permissionByToolUse,
      }
    }
    case 'bridge/permission_resolved': {
      const id = String(frame.request_id)
      const cur = s.permissions[id]
      if (!cur) return s
      return {
        ...s,
        permissions: {
          ...s.permissions,
          [id]: {
            ...cur,
            resolved: {
              behavior: frame.behavior as 'allow' | 'deny' | 'cancelled',
              message: frame.message as string | null,
            },
          },
        },
      }
    }
    case 'bridge/context_usage': {
      const { type: _t, ...rest } = frame
      return { ...s, contextUsage: rest as ContextUsage }
    }
    case 'bridge/mcp_status': {
      const { type: _t, ...rest } = frame
      return { ...s, mcpStatus: rest }
    }
    case 'bridge/stderr': {
      const stderr = [...s.stderr, String(frame.line ?? '')]
      return {
        ...s,
        stderr: stderr.length > MAX_STDERR ? stderr.slice(-MAX_STDERR) : stderr,
      }
    }
    case 'bridge/startup': {
      if (typeof frame.phase !== 'string' || typeof frame.label !== 'string')
        return s
      const phases = frame.phase === 'preparing' ? [] : s.startup
      return {
        ...s,
        startup: [
          ...phases.filter((phase) => phase.phase !== frame.phase),
          { phase: frame.phase, label: frame.label, seq },
        ],
        startupTiming: frame.phase === 'preparing' ? null : s.startupTiming,
      }
    }
    case 'bridge/startup_timing': {
      const record = (value: unknown) =>
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : null
      return {
        ...s,
        startupTiming: {
          seq,
          timestamp: timestamp ?? Date.now() / 1000,
          failed: frame.failed === true,
          api: record(frame.api),
          runner: record(frame.runner),
        },
      }
    }
    case 'bridge/command_error': {
      const error = String(frame.error)
      const connectionLost =
        error === acpConnectionLostCommandError &&
        seq > s.snapshotSeq &&
        seq > s.lifecycleSeq
      return {
        ...s,
        ...(connectionLost
          ? {
              status: 'error' as SessionStatus,
              error,
              lifecycleSeq: Math.max(s.lifecycleSeq, seq),
            }
          : {}),
        commandErrors: [
          ...s.commandErrors,
          {
            command: String(frame.command),
            error,
            seq,
            requestId:
              typeof frame.request_id === 'string'
                ? frame.request_id
                : undefined,
          },
        ].slice(-50),
      }
    }
    default:
      return s
  }
}

/** Requests still waiting on the user, oldest first. */
export const pendingPermissions = (s: BridgeState): PermissionRequest[] =>
  Object.values(s.permissions)
    .filter((p) => !p.resolved)
    .sort((a, b) => a.seq - b.seq)
